import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DASHBOARD } from '@/constants/testIds';
import OriginAIPanel from '@/components/OriginAIPanel';
import InteroperabilityWorkspace from '@/components/InteroperabilityWorkspace';
import ProductionAIPanel from '@/components/ProductionAIPanel';
import HandoffPackageModal from '@/components/HandoffPackageModal';

// UI Fusion Components
import TerminalDrawer from '@/components/TerminalDrawer';
import OrchestratorHeaderBar from '@/components/OrchestratorHeaderBar';
import CollapsibleSidebar from '@/components/CollapsibleSidebar';
import MainSidebar from '@/components/MainSidebar';
import ProjectWorkspaceDrawer from '@/components/ProjectWorkspaceDrawer';
import CapabilityLauncher from '@/components/CapabilityLauncher';
import { getProject } from '@/api/projects';
import apiClient from '@/api/client';
import { useAuth } from '@/context/AuthContext';
import MissionsChatSidebar from '@/components/MissionsChatSidebar';
import ContextLayersWorkspace from '@/components/ContextLayersWorkspace';
import OperationalStatusBadge from '@/components/OperationalStatusBadge';
import { OPERATIONAL_STATUS } from '@/constants/operationalTruth';
import { Stack, ArrowLeft, Robot, Cpu, TerminalWindow, FolderOpen, Lightning, RocketLaunch, StopCircle, DotsThree, DownloadSimple } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ isNavbarCollapsed = false, onToggleNavbar }) => {
  const { user, isAuthenticated, isLoading, openAuthModal } = useAuth();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [handoffModalOpen, setHandoffModalOpen] = useState(false);
  const [handoffMode, setHandoffMode] = useState('replacement');
  const [lastHandoffPackage, setLastHandoffPackage] = useState(null);
  const [replacementEvents, setReplacementEvents] = useState([]);

  // Pending Replacement State (Phase A preparation data)
  const [pendingReplacementId, setPendingReplacementId] = useState(null);
  const [candidateProductionAI, setCandidateProductionAI] = useState(null);
  const [handoffPackagePreview, setHandoffPackagePreview] = useState(null);

  const [demoStarted, setDemoStarted] = useState(false);
  const [connectionModalOpen, setConnectionModalOpen] = useState(false);
  const [connectorsCatalog, setConnectorsCatalog] = useState([]);

  // Ephemeral API Keys (RAM only, no persistence)
  const [originApiKey, setOriginApiKey] = useState('');
  const [productionApiKey, setProductionApiKey] = useState('');

  // Execution State
  const [activeExecution, setActiveExecution] = useState(null);

  // Mission 10 Persistent Project State
  const [activeProjectId, setActiveProjectId] = useState(null);
  const [activeProject, setActiveProject] = useState(null);
  const [projectWorkspaceOpen, setProjectWorkspaceOpen] = useState(false);
  const [pendingRequestId, setPendingRequestId] = useState(null);
  const [isActivityOpen, setIsActivityOpen] = useState(false);

  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(false);
  const [connectionTarget, setConnectionTarget] = useState(null);
  const [connectionMode, setConnectionMode] = useState('api');
  const [connectionProvider, setConnectionProvider] = useState('openai');
  const [connectionName, setConnectionName] = useState('');
  const [connectionModel, setConnectionModel] = useState('');
  const [connectionEndpoint, setConnectionEndpoint] = useState('');
  const [connectionApiKey, setConnectionApiKey] = useState('');
  const [connectionSubmitting, setConnectionSubmitting] = useState(false);

  const fetchCatalog = useCallback(async () => {
    setCatalogLoading(true);
    setCatalogError(false);
    try {
      const response = await axios.get(`${API}/connectors/catalog`);
      setConnectorsCatalog(response.data || []);
      setCatalogError(false);
    } catch (error) {
      toast.error('No se pudo cargar el catálogo dinámico de conectores');
      setConnectorsCatalog([]);
      setCatalogError(true);
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    if (connectionModalOpen) {
      fetchCatalog();
    }
  }, [connectionModalOpen, fetchCatalog]);

  // Outer Drawers State (In-flow Flex Layout Drawers)
  const [isOrchestratorActive, setIsOrchestratorActive] = useState(false);
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(false);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [contextFeeds, setContextFeeds] = useState({
    feedLogs: true,
    feedTelemetry: true,
    feedMemory: true
  });

  // Primary Surface State (Workspace-First)
  const [activeSurface, setActiveSurface] = useState('workspace'); // 'workspace' | 'layers'
  const [workspaceActiveTab, setWorkspaceActiveTab] = useState('preview');
  const [isNavCollapsed, setIsNavCollapsed] = useState(false);

  // Inner Panels Collapsibility (Default to collapsed so Workspace is dominant)
  const [isOriginCollapsed, setIsOriginCollapsed] = useState(true);
  const [isProductionCollapsed, setIsProductionCollapsed] = useState(true);

  // Secondary Actions [...] State
  const [secondaryActionsOpen, setSecondaryActionsOpen] = useState(false);
  const secondaryActionsRef = React.useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (secondaryActionsRef.current && !secondaryActionsRef.current.contains(event.target)) {
        setSecondaryActionsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setSecondaryActionsOpen(false);
      }
    };
    if (secondaryActionsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [secondaryActionsOpen]);

  // Mutual exclusion toggles for narrow viewports (< 1280px)
  const handleToggleLeftSidebar = () => {
    const nextState = !leftSidebarOpen;
    setLeftSidebarOpen(nextState);
    if (nextState && window.innerWidth < 1280) {
      setRightSidebarOpen(false);
    }
  };

  const handleToggleRightSidebar = () => {
    const nextState = !rightSidebarOpen;
    setRightSidebarOpen(nextState);
    if (nextState && window.innerWidth < 1280) {
      setLeftSidebarOpen(false);
    }
  };

  const lastUserIdRef = React.useRef(user?.user_id);

  const currentSessionUserIdRef = React.useRef(user?.user_id ?? null);
  const lastSessionUserIdRef = React.useRef(user?.user_id ?? null);
  currentSessionUserIdRef.current = user?.user_id ?? null;

  const initializeSession = useCallback(async () => {
    if (isLoading) return; // Wait for Auth bootstrap to settle!

    if (!isAuthenticated || !user) {
      lastSessionUserIdRef.current = null;
      setSession(null);
      setMessages([]);
      setLoading(false);
      localStorage.removeItem('vasteon_session_id');
      return;
    }

    const capturedUserId = user.user_id;

    // Hard identity reset if user changed
    if (lastSessionUserIdRef.current && lastSessionUserIdRef.current !== capturedUserId) {
      localStorage.removeItem('vasteon_session_id');
      setSession(null);
      setMessages([]);
    }

    lastSessionUserIdRef.current = capturedUserId;

    try {
      setLoading(true);
      const savedId = localStorage.getItem('vasteon_session_id');
      if (savedId) {
        try {
          const sessionResponse = await apiClient.get(`/sessions/${savedId}`);
          if (currentSessionUserIdRef.current !== capturedUserId) return;

          if (sessionResponse.data && sessionResponse.data.id) {
            if (sessionResponse.data.owner_user_id && sessionResponse.data.owner_user_id !== capturedUserId) {
              localStorage.removeItem('vasteon_session_id');
              setSession(null);
              setMessages([]);
            } else {
              setSession(sessionResponse.data);
              try {
                const messagesResponse = await apiClient.get(`/messages/${savedId}`);
                if (currentSessionUserIdRef.current === capturedUserId) {
                  setMessages(messagesResponse.data);
                }
              } catch (_) {
                // Messages load error is non-fatal
              }
              return;
            }
          }
        } catch (_) {
          if (currentSessionUserIdRef.current !== capturedUserId) return;

          // Saved session no longer exists or unauthorized — fall through to create new
          localStorage.removeItem('vasteon_session_id');
        }
      }

      if (currentSessionUserIdRef.current !== capturedUserId) return;

      const response = await apiClient.post('/sessions', { name: 'Session' });
      if (currentSessionUserIdRef.current !== capturedUserId) return;

      localStorage.setItem('vasteon_session_id', response.data.id);
      setSession(response.data);
      setMessages([]);
    } catch (error) {
      if (currentSessionUserIdRef.current === capturedUserId) {
        setSession(null);
      }
    } finally {
      if (currentSessionUserIdRef.current === capturedUserId) {
        setLoading(false);
      }
    }
  }, [isLoading, isAuthenticated, user]);

  useEffect(() => {
    initializeSession();
  }, [initializeSession]);

  const initializeProject = useCallback(async () => {
    if (isLoading) return; // Wait for Auth bootstrap to settle!

    if (!isAuthenticated || !user) {
      setActiveProject(null);
      setActiveProjectId(null);
      localStorage.removeItem('vasteon_project_id');
      lastUserIdRef.current = null;
      return;
    }

    const capturedUserId = user.user_id;

    // Hard identity reset if user changed
    if (lastUserIdRef.current && lastUserIdRef.current !== capturedUserId) {
      setActiveProject(null);
      setActiveProjectId(null);
      localStorage.removeItem('vasteon_project_id');
    }
    lastUserIdRef.current = capturedUserId;

    const savedProjectId = localStorage.getItem('vasteon_project_id');
    if (savedProjectId) {
      try {
        const proj = await getProject(savedProjectId);
        // Ensure user hasn't switched while request was in-flight
        if (user?.user_id !== capturedUserId) return;

        if (proj && proj.status !== 'deleted') {
          if (proj.owner_user_id && proj.owner_user_id !== capturedUserId) {
            localStorage.removeItem('vasteon_project_id');
            setActiveProject(null);
            setActiveProjectId(null);
            return;
          }
          setActiveProject(proj);
          setActiveProjectId(proj.project_id || proj.id);
          return;
        }
      } catch (err) {
        // Saved project not found, 404, or deleted - clean up
      }
      localStorage.removeItem('vasteon_project_id');
      setActiveProject(null);
      setActiveProjectId(null);
    } else {
      setActiveProject(null);
      setActiveProjectId(null);
    }
  }, [isLoading, isAuthenticated, user]);

  useEffect(() => {
    initializeProject();
  }, [initializeProject]);

  // Synchronize active project with definitive authentication state
  useEffect(() => {
    if (isLoading) return; // DO NOT clear during unresolved Auth bootstrap!

    if (!isAuthenticated || !user) {
      // Definitive anonymous state after bootstrap completes
      setActiveProject(null);
      setActiveProjectId(null);
      localStorage.removeItem('vasteon_project_id');
      lastUserIdRef.current = null;
    } else if (user && activeProject && activeProject.owner_user_id && activeProject.owner_user_id !== user.user_id) {
      // Active project belonged to a different user - clean up
      setActiveProject(null);
      setActiveProjectId(null);
      localStorage.removeItem('vasteon_project_id');
    }
  }, [isLoading, isAuthenticated, user, activeProject]);


  const handleSelectProject = useCallback(async (projectId) => {
    if (!projectId) {
      localStorage.removeItem('vasteon_project_id');
      setActiveProjectId(null);
      setActiveProject(null);
      return;
    }
    try {
      const proj = await getProject(projectId);
      if (proj && proj.status !== 'deleted') {
        localStorage.setItem('vasteon_project_id', proj.project_id || proj.id);
        setActiveProjectId(proj.project_id || proj.id);
        setActiveProject(proj);
      } else {
        localStorage.removeItem('vasteon_project_id');
        setActiveProjectId(null);
        setActiveProject(null);
      }
    } catch (err) {
      localStorage.removeItem('vasteon_project_id');
      setActiveProjectId(null);
      setActiveProject(null);
    }
  }, []);


  const handleStartGuidedDemo = async () => {
    try {
      toast.info('Starting guided demo…');
      const response = await axios.post(`${API}/demo/initialize`);
      const sessionId = response.data.session_id;

      const sessionResponse = await apiClient.get(`/sessions/${sessionId}`);
      setSession(sessionResponse.data);

      const messagesResponse = await apiClient.get(`/messages/${sessionId}`);
      setMessages(messagesResponse.data);

      setDemoStarted(true);
      toast.success('Guided demo initialized');
    } catch (error) {
      toast.error('Failed to start guided demo');
    }
  };

  const handleExitGuidedDemo = async () => {
    setMessages([]);
    setReplacementEvents([]);
    setLastHandoffPackage(null);
    setHandoffModalOpen(false);

    setConnectionModalOpen(false);
    setConnectionTarget(null);
    setConnectionMode('api');
    setConnectionApiKey('');

    setDemoStarted(false);

    localStorage.removeItem('vasteon_session_id');
    await initializeSession();

    toast.success('Guided demo closed');
  };

  const handleConnectAI = async () => {
    if (!session?.id || !connectionTarget) {
      toast.error('No active session or connection target');
      return;
    }

    if (connectionMode === 'api' && catalogError) {
      toast.error('El catálogo de conectores no está disponible');
      return;
    }

    if (connectionMode === 'api' && !connectionApiKey.trim()) {
      toast.error('API key is required');
      return;
    }

    setConnectionSubmitting(true);

    try {
      const payload = {
        session_id: session.id,
        ai_type: connectionTarget,
        provider:
          connectionMode === 'demo'
            ? connectionTarget === 'origin'
              ? 'demo_origin'
              : 'demo_production'
            : connectionProvider,
        name:
          connectionName.trim() ||
          (connectionMode === 'demo'
            ? connectionTarget === 'origin'
              ? 'Demo Origin AI'
              : 'Demo Production AI'
            : `${connectionProvider} ${connectionTarget} AI`),
        mode: connectionMode,
        model: connectionModel.trim() || null,
        endpoint: connectionEndpoint.trim() || null,
        api_key:
          connectionMode === 'api'
            ? connectionApiKey.trim()
            : null,
        capabilities:
          connectionTarget === 'origin'
            ? ['chat', 'analysis']
            : ['chat', 'code_generation', 'analysis'],
      };

      await apiClient.post(
        `/sessions/${session.id}/connect-ai`,
        payload
      );

      const sessionResponse = await apiClient.get(
        `/sessions/${session.id}`
      );

      setSession(sessionResponse.data);

      toast.success(
        `${
          connectionTarget === 'origin'
            ? 'Origin'
            : 'Production'
        } AI connected`
      );

      if (connectionMode === 'api' && connectionApiKey.trim()) {
        if (connectionTarget === 'origin') setOriginApiKey(connectionApiKey.trim());
        else setProductionApiKey(connectionApiKey.trim());
      }
      setConnectionApiKey('');
      setConnectionModalOpen(false);
      setConnectionTarget(null);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message =
        typeof detail === 'string'
          ? detail
          : detail?.error ||
            detail?.message ||
            'Failed to connect AI';

      toast.error(message);
    } finally {
      setConnectionSubmitting(false);
      setConnectionApiKey('');
    }
  };

  const handleDisconnectAI = async (type) => {
    try {
      await apiClient.post(`/sessions/${session.id}/disconnect-ai?ai_type=${type}`);

      const sessionResponse = await apiClient.get(`/sessions/${session.id}`);
      setSession(sessionResponse.data);
      toast.success(`${type === 'origin' ? 'Origin' : 'Production'} AI disconnected`);
      if (type === 'origin') setOriginApiKey('');
      else setProductionApiKey('');
    } catch (error) {
      toast.error('Failed to disconnect AI');
    }
  };

  const handleSendMessage = async (content, aiType = 'production') => {
    if (!session?.id) return;
    if (activeExecution && !['completed', 'failed', 'timed_out', 'cancelled'].includes(activeExecution.status)) {
      toast.error('Hay una ejecución en curso.');
      return;
    }

    const aiConfig = aiType === 'origin' ? session.origin_ai : session.production_ai;
    if (!aiConfig) {
      toast.error('La IA no está conectada.');
      return;
    }

    const apiKey = aiType === 'origin' ? originApiKey : productionApiKey;

    let finalModel = aiConfig.model || 'default';
    try {
      const modelsResponse = await apiClient.post(`/sessions/${session.id}/executable-models`, {
        ai_type: aiType,
        api_key: apiKey || null
      });
      const validModels = modelsResponse.data?.models || [];
      if (validModels.length === 0) {
        toast.error('El proveedor no devolvió modelos ejecutables (catálogo vacío).');
        setActiveExecution({ status: 'failed', error_message: 'Catálogo de modelos vacío' });
        return;
      }
      if (!finalModel || finalModel === 'default' || finalModel === 'Gemini' || !validModels.includes(finalModel)) {
        if (validModels.includes('gemini-3.6-flash')) {
          finalModel = 'gemini-3.6-flash';
        } else {
          const non25Model = validModels.find(m => m !== 'gemini-2.5-flash');
          finalModel = non25Model || validModels[0];
        }
      }
    } catch (e) {
      toast.error('Error verificando modelos ejecutables o credencial inválida.');
      setActiveExecution({ status: 'failed', error_message: 'Error de validación de modelo' });
      return;
    }

    const reqId = pendingRequestId || crypto.randomUUID();
    setPendingRequestId(reqId);
    setActiveExecution({ request_id: reqId, status: 'accepting', ai_type: aiType, content: content });

    try {
      const response = await apiClient.post(`/sessions/${session.id}/executions`, {
        request_id: reqId,
        ai_type: aiType,
        model: finalModel,
        content: content,
        api_key: apiKey || null
      });

      if (response.status === 202) {
        setPendingRequestId(null);
        setActiveExecution(prev => ({ ...prev, id: response.data.execution_id, status: 'pending', cancellable: true }));
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.message || 'Error al solicitar ejecución';
      setActiveExecution({ status: 'failed', error_message: msg });
      toast.error(msg);
    }
  };

  const handleCancelExecution = async () => {
    if (!activeExecution?.id || activeExecution.status === 'cancel_requested') return;

    setActiveExecution(prev => ({ ...prev, status: 'cancel_requested' }));
    try {
      await apiClient.post(`/executions/${activeExecution.id}/cancel`);
      toast.info('Cancelación solicitada...');
    } catch (error) {
      toast.error('No se pudo cancelar la ejecución.');
      setActiveExecution(prev => ({ ...prev, status: prev.status === 'cancel_requested' ? 'pending' : prev.status }));
    }
  };

  useEffect(() => {
    if (!activeExecution?.id) return;
    if (['completed', 'failed', 'timed_out', 'cancelled'].includes(activeExecution.status)) {
      return;
    }

    let isMounted = true;
    let timer = null;

    const poll = async () => {
      try {
        const response = await apiClient.get(`/executions/${activeExecution.id}`);
        if (!isMounted) return;
        const exec = response.data;

        if (['completed', 'failed', 'timed_out', 'cancelled'].includes(exec.status)) {
          if (exec.status === 'completed') {
            const messagesResponse = await apiClient.get(`/messages/${session.id}`);
            if (isMounted) {
              setMessages(messagesResponse.data);
              setActiveExecution(prev => prev?.id === exec.execution_id ? { ...prev, ...exec, status: exec.status } : prev);
              toast.success('Ejecución completada con éxito.');
            }
          } else if (exec.status === 'failed') {
            if (isMounted) setActiveExecution(prev => prev?.id === exec.execution_id ? { ...prev, ...exec, status: exec.status } : prev);
            toast.error(`Ejecución fallida: ${exec.error_message || 'Error desconocido'}`);
          } else if (exec.status === 'timed_out') {
            if (isMounted) setActiveExecution(prev => prev?.id === exec.execution_id ? { ...prev, ...exec, status: exec.status } : prev);
            toast.error('La ejecución excedió el tiempo límite.');
          } else if (exec.status === 'cancelled') {
            if (isMounted) setActiveExecution(prev => prev?.id === exec.execution_id ? { ...prev, ...exec, status: exec.status } : prev);
            toast.info('Ejecución cancelada exitosamente.');
          }
        } else {
          if (isMounted) setActiveExecution(prev => prev?.id === exec.execution_id ? { ...prev, ...exec, status: exec.status } : prev);
          timer = setTimeout(poll, 1500);
        }
      } catch (error) {
        if (!isMounted) return;
        if (error.response && error.response.status !== 500 && error.response.status !== 502 && error.response.status !== 503 && error.response.status !== 504) {
           setActiveExecution(prev => ({ ...prev, status: 'failed', error_message: 'Error de conexión no transitorio.' }));
           toast.error('Polling falló irrevocablemente.');
        } else {
           timer = setTimeout(poll, 2500);
        }
      }
    };

    timer = setTimeout(poll, 1500);

    return () => {
      isMounted = false;
      if (timer) clearTimeout(timer);
    };
  }, [activeExecution?.id, activeExecution?.status, session?.id]);

  const handleRunSandbox = async (executionData) => {
    if (session?.id) {
      try {
        const sessionResponse = await apiClient.get(`/sessions/${session.id}`);
        setSession(sessionResponse.data);
      } catch (e) {
        // ignore background sync error
      }
    }
  };

  const handleApproveResult = async () => {
    const targetProject = activeProject || session?.project;
    const targetId = targetProject?.id || targetProject?.project_id;
    if (!targetId) return;

    try {
      await apiClient.post(`/projects/${targetId}/approve`);

      if (session?.id) {
        const sessionResponse = await apiClient.get(`/sessions/${session.id}`);
        setSession(sessionResponse.data);
      }
      toast.success('Resultado aprobado para producción');
    } catch (error) {
      toast.error('Error al aprobar resultado: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Phase A: Prepare Production AI Replacement
  const handlePrepareReplacement = async () => {
    if (!session?.id) return;

    try {
      toast.info('Preparing Production AI replacement...');
      const response = await apiClient.post(`/sessions/${session.id}/replace-production-ai`);

      const { pending_replacement_id, candidate_production_ai, handoff_package } = response.data;

      setPendingReplacementId(pending_replacement_id);
      setCandidateProductionAI(candidate_production_ai);
      setHandoffPackagePreview(handoff_package);
      setHandoffMode('replacement');

      setReplacementEvents((prev) => [
        ...prev,
        {
          id: Date.now(),
          type: 'warning',
          content: `[REPLACEMENT PREPARED] Candidate AI: ${candidate_production_ai?.name || 'New AI'}. Awaiting human approval.`,
          timestamp: new Date().toISOString()
        }
      ]);

      setHandoffModalOpen(true);
      toast.info('Human approval required to confirm replacement');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to prepare replacement';
      toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    }
  };

  // Phase B: Human Confirms Replacement
  const handleConfirmReplacement = async () => {
    if (!session?.id || !pendingReplacementId) {
      toast.error('No pending replacement to confirm');
      return;
    }

    try {
      const response = await apiClient.post(
        `/sessions/${session.id}/confirm-replacement`,
        { pending_replacement_id: pendingReplacementId }
      );

      const sessionResponse = await apiClient.get(`/sessions/${session.id}`);
      setSession(sessionResponse.data);

      setLastHandoffPackage(response.data.handoff_package);
      setReplacementEvents((prev) => [
        ...prev,
        {
          id: Date.now(),
          type: 'success',
          content: `[REPLACEMENT APPROVED] Production AI replaced. Phase: ${response.data.handoff_package?.current_phase || 'Next'}`,
          timestamp: new Date().toISOString()
        }
      ]);

      setPendingReplacementId(null);
      setCandidateProductionAI(null);
      setHandoffPackagePreview(null);
      setHandoffModalOpen(false);

      toast.success('Production AI replaced successfully!');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to confirm replacement';
      toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    }
  };

  // Phase C: Human Cancels Replacement
  const handleCancelReplacement = async () => {
    if (!session?.id || !pendingReplacementId) {
      setHandoffModalOpen(false);
      return;
    }

    try {
      await apiClient.post(
        `/sessions/${session.id}/cancel-replacement`,
        { pending_replacement_id: pendingReplacementId }
      );

      setReplacementEvents((prev) => [
        ...prev,
        {
          id: Date.now(),
          type: 'system',
          content: `[REPLACEMENT CANCELLED] Human rejected candidate AI. Retaining current Production AI.`,
          timestamp: new Date().toISOString()
        }
      ]);

      setPendingReplacementId(null);
      setCandidateProductionAI(null);
      setHandoffPackagePreview(null);
      setHandoffModalOpen(false);

      toast.info('Replacement cancelled. Current Production AI retained.');
    } catch (error) {
      setHandoffModalOpen(false);
    }
  };

  const handleToggleContextFeed = (feedKey) => {
    setContextFeeds((prev) => ({
      ...prev,
      [feedKey]: !prev[feedKey]
    }));
  };

  const handlePrepareHandoff = async (sourceMessageId, instruction) => {
    if (!session?.id) return;
    try {
      toast.info('Preparing Functional Handoff...');
      const response = await apiClient.post(`/sessions/${session.id}/prepare-handoff`, {
        source_message_id: sourceMessageId || crypto.randomUUID(),
        instruction: instruction
      });
      setHandoffPackagePreview(response.data);
      setHandoffMode('handoff');
      setHandoffModalOpen(true);
    } catch (error) {
      toast.error('Failed to prepare handoff');
    }
  };

  const handleExecuteHandoff = async () => {
    if (!session?.id || !handoffPackagePreview) return;

    // Validar credencial si la IA requiere real
    if (session.production_ai?.mode === 'api' && !productionApiKey) {
      toast.error('Production AI API key required for real handoff');
      return;
    }

    const reqId = pendingRequestId || crypto.randomUUID();
    setPendingRequestId(reqId);

    try {
      const response = await apiClient.post(`/sessions/${session.id}/execute-handoff`, {
        handoff_id: handoffPackagePreview.id || handoffPackagePreview.handoff_id,
        package_hash: handoffPackagePreview.package_hash,
        api_key: productionApiKey || undefined,
        request_id: reqId
      });

      const executionId = response.data.execution_id;
      setLastHandoffPackage({
        ...handoffPackagePreview,
        execution_id: executionId,
        status: response.data.status
      });

      setPendingRequestId(null);
      setHandoffModalOpen(false);

      setActiveExecution({
        id: executionId,
        ai_type: 'production',
        status: 'pending'
      });

      toast.success('Handoff transferido exitosamente');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Failed to execute handoff');

      if (error.response?.status === 409) {
        toast.error('Handoff package is currently being transferred by another request');
      } else {
        toast.error(msg);
      }
    }
  };

  const [exporting, setExporting] = useState(false);

  const handleArchiveProject = async () => {
    if (!session?.project?.id && !activeProjectId) {
      toast.error('Sin proyecto activo para exportar');
      return;
    }

    let blobUrl = null;
    try {
      setExporting(true);
      toast.info('Validando y preparando exportación del proyecto...');

      // Phase 1: Call POST /archive (pre-validates construction & bounds)
      const targetPid = activeProjectId || session?.project?.id;
      const archiveRes = await axios.post(`${API}/projects/${targetPid}/export`);
      const downloadUrl = archiveRes.data?.download_url;

      if (!downloadUrl) {
        throw new Error('URL de descarga no proporcionada por el servidor');
      }

      toast.info('Descargando paquete de artefactos...');
      const fullDownloadUrl = downloadUrl.startsWith('http') ? downloadUrl : `${BACKEND_URL}${downloadUrl}`;

      // Phase 2: Controlled GET request with responseType: 'blob'
      const response = await axios.get(fullDownloadUrl, {
        responseType: 'blob'
      });

      if (response.status !== 200) {
        throw new Error(`Error en descarga (HTTP ${response.status})`);
      }

      // Extract and sanitize filename from Content-Disposition
      let rawFilename = `project_${targetPid ? targetPid.slice(0, 8) : 'export'}.zip`;
      const disposition = response.headers['content-disposition'];
      if (disposition && disposition.includes('filename=')) {
        const match = disposition.match(/filename="?([^";]+)"?/);
        if (match && match[1]) {
          rawFilename = match[1];
        }
      }

      // Neutralize path separators, null bytes, and dangerous characters in download attribute
      let sanitizedFilename = rawFilename.replace(/\\|\//g, '_').replace(/\x00/g, '').replace(/[^a-zA-Z0-9_.-]/g, '_');
      if (!sanitizedFilename || sanitizedFilename === '.' || sanitizedFilename === '..') {
        sanitizedFilename = `project_${targetPid ? targetPid.slice(0, 8) : 'export'}.zip`;
      }

      // Create in-memory Blob URL
      const blob = new Blob([response.data], { type: 'application/zip' });
      blobUrl = window.URL.createObjectURL(blob);

      // Temporary link trigger with guaranteed DOM cleanup
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', sanitizedFilename);
      document.body.appendChild(link);
      try {
        link.click();
      } finally {
        if (link && link.parentNode) {
          link.parentNode.removeChild(link);
        }
      }

      // Sync updated session
      const sessionResponse = await apiClient.get(`/sessions/${session.id}`);
      setSession(sessionResponse.data);

      toast.success('Archivo ZIP exportado exitosamente');
    } catch (error) {
      let errorMessage = 'Error al exportar el proyecto';

      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          errorMessage = parsed.detail || errorMessage;
        } catch {
          errorMessage = 'Fallo en servidor durante la descarga del archivo';
        }
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      if (blobUrl) {
        window.URL.revokeObjectURL(blobUrl);
      }
      setExporting(false);
    }
  };

  return (
    <div className="h-[calc(100vh-3rem)] h-[calc(100dvh-3rem)] flex-1 min-h-0 flex flex-col relative bg-[#0B0D13] overflow-hidden select-none">
      {/* Main Canvas Area (Integrated Flex Row Container) */}
      <div className="flex-1 flex flex-row min-h-0 min-w-0 overflow-hidden relative bg-[#0B0D13]">
        {/* Main Persistent Left Navigation Sidebar (Mission 13-C3C) */}
        <MainSidebar
          activeSurface={activeSurface}
          onSelectSurface={setActiveSurface}
          onOpenProjects={() => {
            if (!isAuthenticated) {
              openAuthModal();
            } else {
              setProjectWorkspaceOpen(true);
            }
          }}
          isCollapsed={isNavCollapsed}
          onToggleCollapse={() => setIsNavCollapsed(!isNavCollapsed)}
          user={user}
        />

        {/* Explorer / Connectors Dock (In-flow on demand) */}
        {leftSidebarOpen && (
          <CollapsibleSidebar
            isOpen={leftSidebarOpen}
            onToggle={handleToggleLeftSidebar}
            activeFilter={activeFilter}
            onSelectFilter={setActiveFilter}
            session={session}
          />
        )}

        {/* Production Workbench (Central Flex Container) */}
        <main className="flex-1 flex flex-col min-w-0 min-h-0 p-2 gap-2 overflow-hidden bg-[#0B0D13]">
          {/* Surface & Tools Unified Toolbar (Level 2) */}
          <div className="flex items-center justify-between px-3 py-1.5 bg-[#12151E] border border-white/[0.08] rounded-lg shrink-0 gap-2 flex-wrap">
            {/* Left: [+] Capability Launcher & Project Context */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Symbolic Capability Launcher [+] (Mission 13-C3B) */}
              <CapabilityLauncher
                hasActiveProject={!!activeProject}
                onOpenProjects={() => {
                  if (!isAuthenticated) {
                    openAuthModal();
                  } else {
                    setProjectWorkspaceOpen(true);
                  }
                }}
                onSelectSurface={setActiveSurface}
                onSelectTab={(tab) => {
                  setActiveSurface('workspace');
                  setWorkspaceActiveTab(tab);
                }}
                onOpenConnectors={() => setLeftSidebarOpen(true)}
                onOpenOrigin={() => setIsOriginCollapsed(false)}
                onOpenProduction={() => setIsProductionCollapsed(false)}
                onOpenChat={() => setRightSidebarOpen(true)}
                onOpenActivity={() => setIsActivityOpen(true)}
              />

              {/* Project Context Badge */}
              {activeProject ? (
                <button
                  type="button"
                  data-testid="current-project-badge"
                  onClick={() => {
                    if (!isAuthenticated) {
                      openAuthModal();
                    } else {
                      setProjectWorkspaceOpen(true);
                    }
                  }}
                  className="flex items-center gap-1.5 px-2 py-0.5 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 border border-cyan-500/20 text-[11px] font-sans font-medium rounded transition-all max-w-[180px] truncate"
                  title={`Proyecto activo: ${activeProject.name}`}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 shrink-0" />
                  <span className="truncate">{activeProject.name}</span>
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="current-project-badge"
                  onClick={() => {
                    if (!isAuthenticated) {
                      openAuthModal();
                    } else {
                      setProjectWorkspaceOpen(true);
                    }
                  }}
                  className="flex items-center gap-1.5 px-2 py-0.5 text-slate-400 hover:text-slate-200 hover:bg-white/[0.04] border border-white/[0.06] text-[11px] font-sans font-medium rounded transition-all"
                  title="Seleccionar o crear un proyecto"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-500 shrink-0" />
                  <span>Sin proyecto</span>
                </button>
              )}
            </div>

            {/* Right: Secondary Actions [...] */}
            <div className="flex items-center gap-1.5">
              {/* Secondary Actions Affordance [...] */}
              <div ref={secondaryActionsRef} className="relative inline-block">
                <button
                  type="button"
                  data-testid="workbench-secondary-actions-btn"
                  aria-label="Más opciones"
                  aria-expanded={secondaryActionsOpen}
                  onClick={() => setSecondaryActionsOpen(!secondaryActionsOpen)}
                  className={`p-1.5 text-xs font-sans font-medium flex items-center justify-center rounded-md border transition-all ${
                    secondaryActionsOpen
                      ? 'bg-white/[0.08] text-white border-white/[0.18]'
                      : 'bg-white/[0.02] text-slate-400 hover:text-slate-200 border-white/[0.06] hover:bg-white/[0.05]'
                  }`}
                  title="Más opciones"
                >
                  <DotsThree size={16} weight="bold" />
                </button>

                {secondaryActionsOpen && (
                  <div
                    data-testid="workbench-secondary-actions-menu"
                    role="menu"
                    aria-label="Menú secundario"
                    className="absolute top-full right-0 mt-1.5 w-56 bg-[#11131A] border border-white/[0.1] rounded-xl shadow-2xl p-1.5 z-50 backdrop-blur-md flex flex-col gap-1 text-xs select-none"
                  >
                    {/* Section: ACCIONES */}
                    <div className="px-2 py-0.5 text-[10px] font-mono font-medium tracking-wider text-slate-500 uppercase">
                      Acciones
                    </div>
                    {activeProject || session?.project ? (
                      <button
                        type="button"
                        role="menuitem"
                        data-testid="secondary-action-export"
                        disabled={exporting}
                        onClick={() => {
                          setSecondaryActionsOpen(false);
                          handleArchiveProject();
                        }}
                        className="w-full px-2.5 py-1.5 flex items-center gap-2 rounded-md hover:bg-white/[0.06] text-slate-300 hover:text-white transition-colors text-left"
                      >
                        <DownloadSimple size={14} className="text-slate-400" />
                        <span>Exportar proyecto (ZIP)</span>
                      </button>
                    ) : (
                      <div className="px-2.5 py-1 text-slate-500 text-[11px] font-sans">
                        Sin acciones activas
                      </div>
                    )}

                    {/* Section: FUTURO */}
                    <div className="pt-1 mt-1 border-t border-white/[0.05]">
                      <div className="px-2 py-0.5 text-[10px] font-mono font-medium tracking-wider text-slate-500 uppercase flex items-center justify-between">
                        <span>Futuro</span>
                        <OperationalStatusBadge status={OPERATIONAL_STATUS.STATIC} size="sm" />
                      </div>
                      <button
                        type="button"
                        role="menuitem"
                        data-testid="secondary-action-orchestrator"
                        onClick={() => {
                          setSecondaryActionsOpen(false);
                          toast.info('Modo Orquestador: Capacidad en fase de diseño preliminar (Static / Future)');
                        }}
                        className="w-full px-2.5 py-1.5 flex items-center justify-between rounded-md hover:bg-white/[0.06] text-slate-400 hover:text-slate-200 transition-colors text-left"
                      >
                        <div className="flex items-center gap-2">
                          <Lightning size={14} className="text-amber-400/80" />
                          <span>Orquestador</span>
                        </div>
                        <span className="text-[9px] font-mono text-slate-500">Static</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Workbench Body Row */}
          <div className="flex-1 flex flex-row min-w-0 min-h-0 gap-3 overflow-hidden">
            {/* Column 1: Origin AI (Collapsible On Demand) */}
            {!isOriginCollapsed && (
              <div className="h-full w-80 shrink-0 transition-all duration-300">
                <OriginAIPanel
                  session={session}
                  messages={messages}
                  onConnect={() => {
                    setConnectionTarget('origin');
                    setConnectionModalOpen(true);
                  }}
                  onDisconnect={() => handleDisconnectAI('origin')}
                  onSendMessage={(content) => handleSendMessage(content, 'origin')}
                  onHandoffRequest={handlePrepareHandoff}
                  activeExecution={activeExecution?.ai_type === 'origin' ? activeExecution : null}
                  onCancelExecution={handleCancelExecution}
                  isCollapsed={isOriginCollapsed}
                  onToggleCollapse={() => setIsOriginCollapsed(!isOriginCollapsed)}
                />
              </div>
            )}

            {/* Central Dominant Surface: Workspace OR Context Layers */}
            <div className="h-full flex-1 min-w-0 min-h-0 overflow-hidden flex flex-col">
              {activeSurface === 'workspace' ? (
                <InteroperabilityWorkspace
                  session={session}
                  project={activeProject}
                  messages={messages}
                  onRunSandbox={handleRunSandbox}
                  onApproveResult={handleApproveResult}
                  onArchiveProject={handleArchiveProject}
                  exporting={exporting}
                  activeTab={workspaceActiveTab}
                  onTabChange={setWorkspaceActiveTab}
                />
              ) : (
                <div data-testid="context-layers-surface" className="h-full flex-1 min-h-0 flex flex-col bg-[#0A0C10] border border-[#1F2937] overflow-hidden">
                  <div className="p-3 bg-[#13131A] border-b border-[#1F2937] flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-3">
                      <Stack size={16} className="text-[#00E5FF]" />
                      <h2 className="text-sm font-bold terminal-font text-[#F3F4F6]">
                        {activeProject?.name || 'Superficie de Capas de Contexto'}
                      </h2>
                      <OperationalStatusBadge status={OPERATIONAL_STATUS.STATIC} label="STATIC / VISUAL MODEL" size="sm" />
                    </div>
                    <button
                      data-testid="return-to-workspace-btn"
                      onClick={() => setActiveSurface('workspace')}
                      className="px-3 py-1 text-xs font-mono font-bold bg-[#00E5FF] text-[#0A0C10] hover:bg-[#00E5FF]/90 flex items-center gap-1.5 transition-all shadow"
                    >
                      <ArrowLeft size={14} />
                      <span>Volver a Workspace</span>
                    </button>
                  </div>
                  <div className="flex-1 overflow-hidden p-2 min-h-0 flex flex-col">
                    <ContextLayersWorkspace />
                  </div>
                </div>
              )}
            </div>

            {/* Column 3: Production AI (Collapsible On Demand) */}
            {!isProductionCollapsed && (
              <div className="h-full w-80 shrink-0 transition-all duration-300">
                <ProductionAIPanel
                  session={session}
                  messages={messages}
                  onConnect={() => {
                    setConnectionTarget('production');
                    setConnectionModalOpen(true);
                  }}
                  onDisconnect={() => handleDisconnectAI('production')}
                  onReplace={handlePrepareReplacement}
                  onSendMessage={(content) => handleSendMessage(content, 'production')}
                  activeExecution={activeExecution?.ai_type === 'production' ? activeExecution : null}
                  onCancelExecution={handleCancelExecution}
                  extraLogs={replacementEvents}
                  handoffPackage={lastHandoffPackage}
                  isCollapsed={isProductionCollapsed}
                  onToggleCollapse={() => setIsProductionCollapsed(!isProductionCollapsed)}
                />
              </div>
            )}
          </div>
        </main>

        {/* Missions & Chat Dock (In-flow Flex On Demand) */}
        {rightSidebarOpen && (
          <MissionsChatSidebar
            isOpen={rightSidebarOpen}
            onToggle={handleToggleRightSidebar}
            messages={messages}
            onSendMessage={(content) => handleSendMessage(content, 'production')}
            activeExecution={activeExecution?.ai_type === 'production' ? activeExecution : null}
          />
        )}
      </div>

      {/* Mission 10 Persistent Project Workspace Drawer */}
      <ProjectWorkspaceDrawer
        isOpen={projectWorkspaceOpen}
        onClose={() => setProjectWorkspaceOpen(false)}
        activeProjectId={activeProjectId}
        onSelectProject={handleSelectProject}
        sessionId={session?.id}
      />

      {/* Unified Bottom Dock (Operations Activity Console) */}
      <TerminalDrawer
        isOpen={isActivityOpen}
        onToggle={setIsActivityOpen}
        events={messages}
        activeProjectId={activeProjectId || session?.project?.id || session?.project?.project_id}
        project={activeProject || session?.project}
        onRunCommand={(cmd) => {
          toast.info(`Comando ejecutado en terminal: ${cmd}`);
        }}
      />

      {/* Modal Conectar IA */}
      {connectionModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-xs">
          <div className="w-full max-w-lg rounded-lg border border-[#00E5FF]/40 bg-[#11131A] p-6 shadow-2xl">
            <div className="mb-5">
              <h2 className="terminal-font text-xl font-bold text-white">
                Conectar IA de{' '}
                {connectionTarget === 'origin'
                  ? 'origen'
                  : 'producción'}
              </h2>

              <p className="mt-2 text-sm text-[#9CA3AF]">
                Selecciona una demostración o verifica una conexión
                real mediante API.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm text-white">
                  Tipo de conexión
                </label>

                <select
                  value={connectionMode}
                  onChange={(event) =>
                    setConnectionMode(event.target.value)
                  }
                  className="w-full rounded border border-[#374151] bg-[#0A0C10] px-3 py-2 text-white"
                >
                  <option value="demo">Modo demostración</option>
                  <option value="api">API real</option>
                </select>
              </div>

              {connectionMode === 'api' && (
                <>
                  <div>
                    <label className="mb-2 block text-sm text-white">
                      Proveedor
                    </label>

                    <select
                      value={catalogError ? '' : connectionProvider}
                      onChange={(event) =>
                        setConnectionProvider(event.target.value)
                      }
                      disabled={catalogLoading || catalogError}
                      className="w-full rounded border border-[#374151] bg-[#0A0C10] px-3 py-2 text-white disabled:opacity-50"
                    >
                      {catalogLoading ? (
                        <option value="" disabled>Cargando catálogo...</option>
                      ) : catalogError ? (
                        <option value="" disabled>Catálogo no disponible</option>
                      ) : connectorsCatalog.length === 0 ? (
                        <option value="" disabled>No hay conectores disponibles</option>
                      ) : (
                        connectorsCatalog
                          .filter((item) => item.id !== 'demo')
                          .map((item) => (
                            <option
                              key={item.id}
                              value={item.id}
                              disabled={!item.adapter_available}
                            >
                              {item.display_name} ({item.operational_status}
                              {!item.adapter_available ? ' - No disponible' : ''})
                            </option>
                          ))
                      )}
                    </select>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm text-white">
                      Nombre de la conexión
                    </label>

                    <input
                      type="text"
                      value={connectionName}
                      onChange={(event) =>
                        setConnectionName(event.target.value)
                      }
                      placeholder="Ejemplo: OpenAI de producción"
                      className="w-full rounded border border-[#374151] bg-[#0A0C10] px-3 py-2 text-white"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm text-white">
                      Modelo
                    </label>

                    <input
                      type="text"
                      value={connectionModel}
                      onChange={(event) =>
                        setConnectionModel(event.target.value)
                      }
                      placeholder="Ejemplo: gpt-4.1"
                      className="w-full rounded border border-[#374151] bg-[#0A0C10] px-3 py-2 text-white"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm text-white">
                      Clave API
                    </label>

                    <input
                      type="password"
                      value={connectionApiKey}
                      onChange={(event) =>
                        setConnectionApiKey(event.target.value)
                      }
                      placeholder="sk-..."
                      className="w-full rounded border border-[#374151] bg-[#0A0C10] px-3 py-2 text-white font-mono text-sm"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setConnectionModalOpen(false);
                  setConnectionTarget(null);
                }}
                className="rounded border border-[#374151] px-4 py-2 text-sm text-[#9CA3AF] hover:text-white"
              >
                Cancelar
              </button>

              <button
                type="button"
                disabled={connectionSubmitting || (connectionMode === 'api' && (catalogLoading || catalogError))}
                onClick={handleConnectAI}
                className="rounded bg-[#00E5FF] px-4 py-2 text-sm font-bold text-[#0A0C10] hover:bg-[#00E5FF]/90 disabled:opacity-50"
              >
                {connectionSubmitting
                  ? 'Guardando...'
                  : 'Guardar conexión'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Handoff Package Confirmation Modal (Production AI Replacement / Functional Handoff) */}
      <HandoffPackageModal
        open={handoffModalOpen}
        onOpenChange={(val) => { if(!val) handoffMode === 'handoff' ? setHandoffModalOpen(false) : handleCancelReplacement(); }}
        session={session}
        mode={handoffMode}
        candidateAI={candidateProductionAI}
        handoffPackage={handoffPackagePreview}
        onConfirm={handoffMode === 'handoff' ? handleExecuteHandoff : handleConfirmReplacement}
        onCancel={handoffMode === 'handoff' ? () => setHandoffModalOpen(false) : handleCancelReplacement}
      />
    </div>
  );
};

export default Dashboard;
